import { GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid'
import React, { useState } from 'react'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from '@mui/material'
import { com } from '@eidu/web'
import { PersonAdd, PersonAddOutlined } from '@mui/icons-material'
import { useSnackBar } from '../feedback/SnackBarProvider'
import useInviteAuthUser from '../../io/useInviteAuthUser'
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString
import InvitationStatus = com.eidu.sharedlib.entity.user.InvitationStatus
import invitationStatusFromJsonString = com.eidu.sharedlib.entity.user.invitationStatusFromJsonString

export type CellContent = {
  value: string
  error?: string
}

type RenderCellFunction = (
  params: GridRenderCellParams<GridValidRowModel, CellContent | string>
) => React.JSX.Element | null

const getRenderInvitationCell =
  (onInvitationSent?: () => void): RenderCellFunction =>
  ({ row, value }) => {
    const { showSnackBar } = useSnackBar()
    const { invite, isPending: loading } = useInviteAuthUser()
    const [openDialog, setOpenDialog] = useState(false)

    const rawInvitationStatus = typeof value === 'object' ? value.value : value

    // Parse the serialized InvitationStatus data
    let invitationStatus: InvitationStatus | null = null
    try {
      if (rawInvitationStatus && rawInvitationStatus !== 'null') {
        invitationStatus = invitationStatusFromJsonString(rawInvitationStatus)
      }
    } catch (error) {
      console.error(error)
    }

    // Get the status value from the parsed object or use the raw value as fallback

    const entityId = entityIdFromString(row.id)
    const { phoneNumberLoginIdentifier: phoneNumber, emailLoginIdentifier: email } = row

    // Check if authentication credentials are present
    const hasCredentials = phoneNumber || email

    const sendInvitation = async (event?: React.MouseEvent) => {
      if (event) {
        event.stopPropagation()
      }
      try {
        await invite(entityId)

        onInvitationSent?.()

        let message = 'Invitation sent successfully'
        if (email && !phoneNumber) {
          message = 'Invitation email sent successfully'
        } else if (phoneNumber && !email) {
          message = 'Invitation SMS sent successfully'
        }

        showSnackBar(message, 'success')
      } catch (error) {
        showSnackBar('Failed to send invitation', 'error')
        console.error(error)
      } finally {
        setOpenDialog(false)
      }
    }

    const handleInvite = async (event: React.MouseEvent) => {
      // Don't do anything if already loading
      if (loading) {
        return
      }

      // Stop propagation to prevent row click
      event.stopPropagation()

      // For re-sending invitations, show confirmation dialog
      if (invitationStatus === InvitationStatus.Success || invitationStatus === InvitationStatus.Failure) {
        setOpenDialog(true)
        return
      }

      sendInvitation(event)
    }

    const handleCloseDialog = (_event: {}, _reason: 'backdropClick' | 'escapeKeyDown') => {
      setOpenDialog(false)
    }

    const handleCancelClick = (event: React.MouseEvent) => {
      event.stopPropagation()
      setOpenDialog(false)
    }

    // Don't show button if no credentials are present
    if (!hasCredentials || invitationStatus === null) {
      return null
    }

    const color =
      invitationStatus === InvitationStatus.Success
        ? 'success'
        : invitationStatus === InvitationStatus.Failure
          ? 'error'
          : 'gray'
    const icon = invitationStatus === InvitationStatus.Pending ? <PersonAddOutlined /> : <PersonAdd />

    return (
      <>
        <IconButton
          color={color}
          onClick={(event) => handleInvite(event)}
          disabled={loading}
          data-testid="user-invitation-button"
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : icon}
        </IconButton>

        <Dialog open={openDialog} onClose={handleCloseDialog} data-testid="user-invitation-dialog">
          <DialogTitle>Re-send Invitation</DialogTitle>
          <DialogContent>
            <DialogContentText>Do you want to re-send the invitation to this user?</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelClick} disabled={loading} data-testid="user-invitation-cancel">
              Cancel
            </Button>
            <Button onClick={(e) => sendInvitation(e)} disabled={loading} data-testid="user-invitation-resend">
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Re-send'}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    )
  }

export default getRenderInvitationCell
